import { useEffect } from "react";
import { nextDemoId, preloadDemo } from "@/demos/registry";
import type { DemoId } from "@/demos/types";
import type { RuntimePhase } from "@/runtime/types";

/**
 * Fetch the next demo's model while the visitor is busy with the current one.
 * Pressing Next then costs nothing, instead of stalling on several megabytes
 * at the exact moment the visitor asked to move on.
 *
 * Deliberately fire-and-forget: a failed prefetch must not surface anywhere.
 * If it did fail, pressing Next retries it with real error handling attached.
 */
export function useDemoPreloading(activeDemoId: DemoId, phase: RuntimePhase): void {
  useEffect(() => {
    if (phase !== "placed") return;

    // Wait for the current demo to settle before competing for bandwidth.
    const timer = window.setTimeout(() => {
      void preloadDemo(nextDemoId(activeDemoId)).catch(() => undefined);
    }, 1_200);

    return () => window.clearTimeout(timer);
  }, [activeDemoId, phase]);
}
